import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Plus } from 'lucide-react';
import { z } from 'zod';

import { Page } from '@/components/layout/Page';
import { Section } from '@/components/layout/Section';
import { PageHeader } from '@/components/composed/PageHeader';
import { FilterBar } from '@/components/composed/FilterBar';
import { Button } from '@/components/ui/button';
import { TpaTable, TpaFormDialog, tpaListQuery } from '@/features/tpas';
import type { Tpa, TpaListParams } from '@/lib/api/domains/tpas';
import { canEdit } from '@/lib/permissions';
import { useAuthStore } from '@/lib/session/auth-store';
import { usePageMeta } from '@/app/shell/use-page-meta';

const searchSchema = z.object({
  page: z.number().int().min(1).catch(1),
  pageSize: z.number().int().min(1).max(100).catch(25),
  sort: z.string().optional(),
  q: z.string().optional(),
});

export const Route = createFileRoute('/_auth/tpas/')({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData(tpaListQuery(toParams(deps))),
  component: TpasPage,
});

function toParams(search: z.infer<typeof searchSchema>): TpaListParams {
  return {
    page: search.page,
    pageSize: search.pageSize,
    ...(search.sort ? { sort: search.sort } : {}),
    ...(search.q ? { q: search.q } : {}),
  };
}

function TpasPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const session = useAuthStore((s) => s.session);
  const [createOpen, setCreateOpen] = useState(false);

  function setSearch(patch: Partial<z.infer<typeof searchSchema>>) {
    void navigate({ search: (prev) => ({ ...prev, ...patch }) });
  }

  usePageMeta({ title: 'TPAs' });

  // Row/edit open the full detail page (TPAs use a sectioned detail view, not
  // an inspect sheet), landing on the first section.
  function openTpa(tpa: Tpa) {
    void navigate({
      to: '/tpas/$tpaId',
      params: { tpaId: tpa.id },
    });
  }

  return (
    <Page bleed>
      <PageHeader
        title="TPAs"
        actions={
          canEdit(session) ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" />
              New TPA
            </Button>
          ) : null
        }
      />

      <Section>
        <FilterBar
          search={search.q ?? ''}
          onSearchChange={(q) => setSearch({ q: q || undefined, page: 1 })}
          searchLabel="Search"
          searchPlaceholder="Search by name…"
          onClear={
            search.q ? () => setSearch({ q: undefined, page: 1 }) : undefined
          }
        />
        <TpaTable
          params={toParams(search)}
          onPageChange={(page) => setSearch({ page })}
          onRowClick={openTpa}
          onEdit={openTpa}
        />
      </Section>

      <TpaFormDialog open={createOpen} onOpenChange={setCreateOpen} />
    </Page>
  );
}
